'use client';

import { FormEvent, useState } from 'react';
import Image from 'next/image';
import { TeaserNewsletter as TeaserNewsletterType } from '@/shared/types/teasers';
import HIHRichText from '../base/RichText';
import Modal from '../base/Modal';
import {
  ARIA_LABEL_NEWSLETTER_EMAIL,
  ARIA_LABEL_NEWSLETTER_SUCCESS,
  ERROR_NEWSLETTER_EXISTS,
  ERROR_NEWSLETTER_GENERIC,
} from '@/shared/constants';
import HIHButton from '../base/HIHButton';
import Star from '../base/Star';
import Underline from '../base/Underline';
import styles from './TeaserNewsletter.module.scss';

function SuccessMessage() {
  return (
    <div role="alert" className={styles.successMessage}>
      <div>
        <h2 className={styles.successTitle}>
          Willkommen <Underline variation="singleLine">an Bord!</Underline>
        </h2>
        <p className={styles.successCopy}>
          Prüfe deinen Posteingang und den Spamordner, um deine Anmeldung zu bestätigen.
        </p>
      </div>

      <Image
        className={styles.successImage}
        src="/images/newsletter.svg"
        width="447"
        height="386"
        alt=""
      />
    </div>
  );
}

export default function TeaserNewsletter({
  teaser,
  className,
}: {
  teaser: TeaserNewsletterType;
  className?: string;
}) {
  const { title, copy, placeholder, disclaimer, buttonLabel } = teaser;
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<React.ReactElement>(<></>);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    setHasError(false);

    const email = (event.currentTarget.elements.namedItem('email') as HTMLInputElement)
      .value;

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to add subscriber');
      }

      // success
      if (response.status === 201) {
        setModalContent(<SuccessMessage />);
        setIsModalOpen(true);
      }

      // active | unconfirmed
      if (response.status === 200) {
        setErrorMessage(ERROR_NEWSLETTER_EXISTS);
        setHasError(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(ERROR_NEWSLETTER_GENERIC);
        setHasError(true);
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div
      className={[styles.teaserNewsletter, className && className]
        .filter(Boolean)
        .join(' ')}
    >
      <Star rays={8} className={styles.starPosition} />

      <div className={styles.card}>
        <div className={styles.textColumn}>
          <div className={styles.title}>
            {/* {title} */}
            Bleib dran: Der Heute in <br className={styles.brLgOnly} />
            Hamburg-
            <Underline variation="singleLine">Newsletter</Underline>
          </div>
          <p className={styles.copy}>
            {/* {copy} */}
            Du bekommst unsere tägliche Auswahl an Freizeit-Empfehlungen{' '}
            <br className={styles.brXlOnly} />
            ganz bequem in dein E-Mail-Postfach. So verpasst du kein Event mehr.
          </p>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.inputWrapper}>
              <input
                aria-label={ARIA_LABEL_NEWSLETTER_EMAIL}
                required
                type="email"
                name="email"
                aria-describedby={hasError ? 'emailError' : undefined}
                placeholder={placeholder}
                className={[styles.emailInput, hasError ? styles.hasError : '']
                  .filter(Boolean)
                  .join(' ')}
              />

              {hasError && (
                <Image
                  src="/icons/icon-error.svg"
                  alt=""
                  width={24}
                  height={24}
                  className={styles.errorIcon}
                />
              )}
            </div>

            <div className={styles.feedbackRow}>
              {hasError && (
                <div id="emailError" aria-live="polite" className={styles.errorMessage}>
                  {errorMessage}
                </div>
              )}

              <HIHRichText className={styles.disclaimer} value={disclaimer} />
            </div>

            <HIHButton
              title={buttonLabel}
              variant="secondary"
              className={styles.submitButton}
              submit
              isLoading={isSubmitting}
            />
          </div>
        </form>
      </div>

      <div role="alert" aria-live="assertive">
        <Modal
          isOpen={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          ariaLabel={ARIA_LABEL_NEWSLETTER_SUCCESS}
          closeLabel="Alles klar"
          content={modalContent}
        />
      </div>
    </div>
  );
}
